import { useState, useEffect, useRef } from 'react'
import { View, Text, ScrollView, TouchableOpacity, Animated, PanResponder, Dimensions, StyleSheet, useWindowDimensions, Platform } from 'react-native'
import { Gesture, GestureDetector } from 'react-native-gesture-handler'
import ReanimatedAnimated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withSpring,
  runOnJS,
  interpolate,
  Extrapolation,
  Easing,
} from 'react-native-reanimated'
import { useTranslation } from 'react-i18next'
import { colors, fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { romeInstant, romeToday } from '../../lib/romeTime'

// ─── Re-export SectionLabel condiviso ─────────────────────────────────
export { SectionLabel } from '../ui'

// ─── Cache key ────────────────────────────────────────────────────────
export const CACHE_KEY = (leagueId) => `home_data_${leagueId}`

// ─── processPodio ─────────────────────────────────────────────────────
export function processPodio(podioData) {
  const raceMap = {}
  podioData?.forEach((row) => {
    const key = row.id_race
    if (!raceMap[key] || row.id_stage_number > raceMap[key].id_stage_number) {
      raceMap[key] = {
        id_stage: row.id_stage, id_race: row.id_race, race_name: row.race_name,
        id_stage_number: row.id_stage_number, id_type_race: row.id_type_race,
        stage_date: row.stage_date, stage_time: row.stage_time,
        podioTappa: [], podioGc: [],
      }
    }
  })
  podioData?.forEach((row) => {
    const entry = raceMap[row.id_race]
    if (!entry || row.id_stage !== entry.id_stage) return
    if (row.tipo_gara === 'singola' || row.tipo_gara === 'tour_tappa') entry.podioTappa.push(row)
    else if (row.tipo_gara === 'tour_gc') entry.podioGc.push(row)
  })
  Object.values(raceMap).forEach((s) => {
    s.podioTappa = s.podioTappa.sort((a, b) => a.posizione - b.posizione).slice(0, 3)
    s.podioGc = s.podioGc.sort((a, b) => a.posizione - b.posizione).slice(0, 3)
  })
  return Object.values(raceMap).sort((a, b) =>
    new Date(`${b.stage_date}T${b.stage_time}`) - new Date(`${a.stage_date}T${a.stage_time}`)
  )
}

// ─── useCountdown ─────────────────────────────────────────────────────
function computeDiff(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null
  const ms = romeInstant(dateStr, timeStr) - Date.now()   // ora di Roma, non quella del telefono
  if (ms <= 0) return null
  const s = Math.floor(ms / 1000)
  return {
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
    urgent: s < 7200,
  }
}

export function useCountdown(dateStr, timeStr) {
  // Inizializzatore "lazy" di useState: calcola il primo valore SUBITO,
  // durante il render, invece di partire da null e aspettare che
  // l'useEffect qui sotto giri dopo il commit. Con lo stack di
  // PickStackCarousel una PickCard viene rimontata da zero ogni volta che
  // la sua gara passa da "dietro" a "davanti" (cambia genitore React) — con
  // un valore iniziale null il countdown spariva per un istante ad ogni
  // rimontaggio (la card si accorciava, si intravedeva la sezione sotto),
  // dando uno sfarfallio ben visibile proprio durante lo swipe.
  const [diff, setDiff] = useState(() => computeDiff(dateStr, timeStr))
  useEffect(() => {
    setDiff(computeDiff(dateStr, timeStr))
    if (!dateStr || !timeStr) return
    const id = setInterval(() => setDiff(computeDiff(dateStr, timeStr)), 1000)
    return () => clearInterval(id)
  }, [dateStr, timeStr])
  return diff
}

// ─── CronoDisplay ─────────────────────────────────────────────────────
export function CronoDisplay({ date, time }) {
  const tc = useThemeColors()
  const { width } = useWindowDimensions()
  const diff = useCountdown(date, time)
  if (!diff) return null
  const col = diff.urgent ? tc.rosa : tc.text
  const pad = (v) => String(v).padStart(2, '0')

  // Dimensioni proporzionali alla larghezza schermo
  const isWeb = Platform.OS === 'web'
  const numSize = isWeb ? 60 : Math.round(width * 0.11)
  const sepSize = isWeb ? 46 : Math.round(width * 0.09)
  const lineH = numSize + 6

  return (
    <View style={styles.cronoWrap}>
      {diff.days > 0 && (
        <>
          <Text style={[styles.cronoNum, { color: col, fontSize: numSize, lineHeight: lineH }]}>{pad(diff.days)}</Text>
          <Text style={[styles.cronoSep, { color: col, fontSize: sepSize, lineHeight: lineH }]}>:</Text>
        </>
      )}
      <Text style={[styles.cronoNum, { color: col, fontSize: numSize, lineHeight: lineH }]}>{pad(diff.hours)}</Text>
      <Text style={[styles.cronoSep, { color: col, fontSize: sepSize, lineHeight: lineH }]}>:</Text>
      <Text style={[styles.cronoNum, { color: col, fontSize: numSize, lineHeight: lineH }]}>{pad(diff.minutes)}</Text>
      <Text style={[styles.cronoSep, { color: col, fontSize: sepSize, lineHeight: lineH }]}>:</Text>
      <Text style={[styles.cronoNum, { color: col, fontSize: numSize, lineHeight: lineH }]}>{pad(diff.seconds)}</Text>
    </View>
  )
}

// ─── Skeleton ─────────────────────────────────────────────────────────
export function Skeleton({ width = '100%', height = 16, radius = 8, style = {} }) {
  const tc = useThemeColors()
  const opacity = useRef(new Animated.Value(0.3)).current
  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, { toValue: 0.7, duration: 700, useNativeDriver: false }),
        Animated.timing(opacity, { toValue: 0.3, duration: 700, useNativeDriver: false }),
      ])
    ).start()
  }, [opacity])
  return (
    <Animated.View style={[{ width, height, borderRadius: radius, backgroundColor: tc.border, opacity }, style]} />
  )
}

export function SkeletonHome() {
  const tc = useThemeColors()
  return (
    <View>
      <View style={{ marginBottom: 24 }}>
        <Skeleton width={120} height={12} style={{ marginBottom: 12 }} />
        <View style={{ borderRadius: 14, borderWidth: 1, backgroundColor: tc.bgCard, borderColor: tc.border, padding: 16 }}>
          <Skeleton width="70%" height={18} style={{ marginBottom: 12 }} />
          <Skeleton width="50%" height={40} style={{ marginBottom: 12 }} />
          <Skeleton width="30%" height={32} style={{ alignSelf: 'flex-end' }} />
        </View>
      </View>
      <View style={{ marginBottom: 24 }}>
        <Skeleton width={100} height={12} style={{ marginBottom: 12 }} />
        <View style={{ borderRadius: 14, borderWidth: 1, backgroundColor: tc.bgCard, borderColor: tc.border, padding: 16 }}>
          <Skeleton width="60%" height={20} style={{ marginBottom: 10 }} />
          {[1, 2, 3].map(i => (
            <View key={i} style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 }}>
              <Skeleton width="40%" height={14} />
              <Skeleton width="20%" height={14} />
            </View>
          ))}
        </View>
      </View>
    </View>
  )
}

// ─── Carousel ─────────────────────────────────────────────────────────
// Versione originale, invariata — usata ovunque tranne che in PICK DA FARE
// quando ci sono più di 2 gare (in quel caso si usa PickStackCarousel sotto).
const { width: SCREEN_WIDTH } = Dimensions.get('window')

export function Carousel({ children }) {
  const tc = useThemeColors()
  const scrollRef = useRef(null)
  const [idx, setIdx] = useState(0)
  const [cardWidth, setCardWidth] = useState(SCREEN_WIDTH)
  const arr = Array.isArray(children) ? children.filter(Boolean) : [children]
  const count = arr.length
  if (count <= 1) return <View>{arr[0]}</View>

  const onLayout = (e) => {
    const w = e.nativeEvent.layout.width
    if (w > 0) setCardWidth(w)
  }

  const onScroll = (e) => {
    const x = e.nativeEvent.contentOffset.x
    const newIdx = Math.round(x / cardWidth)
    if (newIdx !== idx) setIdx(newIdx)
  }

  const goTo = (i) => {
    scrollRef.current?.scrollTo({ x: i * cardWidth, animated: true })
    setIdx(i)
  }

  return (
    <View onLayout={onLayout}>
      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        onMomentumScrollEnd={onScroll}
        scrollEventThrottle={16}
        decelerationRate="fast"
        snapToInterval={cardWidth}
        snapToAlignment="start"
        disableIntervalMomentum
        style={{ borderRadius: 14, overflow: 'hidden' }}
      >
        {arr.map((child, i) => (
          <View key={i} style={{ width: cardWidth }}>
            {child}
          </View>
        ))}
      </ScrollView>
      <View style={styles.dots}>
        {arr.map((_, i) => (
          <TouchableOpacity key={i} onPress={() => goTo(i)} hitSlop={{ top: 8, bottom: 8, left: 4, right: 4 }}>
            <View style={[
              styles.dot,
              { backgroundColor: i === idx ? tc.rosa : tc.dotInactive },
              i === idx && styles.dotActive,
            ]} />
          </TouchableOpacity>
        ))}
      </View>
    </View>
  )
}

// ─── PickStackCarousel ──────────────────────────────────────────────────
// Stack stile Apple Wallet, SOLO per "PICK DA FARE" quando ci sono più di 2
// gare da pickare — se sono ≤2 si usa il Carousel normale sopra. La card in
// cima è trascinabile — se la swipi abbastanza, si scambia di posto con
// quella dietro: la card davanti indietreggia nell'angolo scurendosi mentre
// quella dietro avanza al centro schiarendosi, mai una sparisce. Ciclico.
//
// Gesture tracciato con react-native-gesture-handler e animazioni con
// react-native-reanimated (entrambi già dipendenze del progetto, qui usati
// per la prima volta in codice nostro) invece di PanResponder + Animated
// (JS-driven): il drag gira sullo UI thread nativo, non su quello JS, per
// un tracking del dito fluido anche quando il JS thread ha altro da fare.
const SWIPE_THRESHOLD = 90    // px di drag orizzontale per completare lo scambio
const STACK_OFFSET_X = 28     // di quanto sbircia a destra la card dietro
const STACK_OFFSET_Y = 26     // di quanto sbircia IN ALTO la card dietro
const SWAP_DURATION = 260     // ms, durata fissa dello scambio sopra soglia
// NOTA: la card dietro resta ferma a top:0 e sbircia a destra (translateX),
// mentre la card DAVANTI viene spinta più in basso (marginTop) — così si
// vede la card dietro sia sopra che a destra, ad angolo, come nel Wallet.
// NOTA: niente scale sulla card dietro — con una riduzione di scala centrata,
// per card larghe/alte l'offset risultava sempre più PICCOLO della
// riduzione di dimensione, quindi il bordo destro/basso della card
// rimpicciolita finiva DENTRO i confini di quella davanti anziché sporgere:
// invisibile. Senza scale, l'offset garantisce sempre uno sbircio visibile
// a prescindere dalle dimensioni reali della card.

export function PickStackCarousel({ children }) {
  const tc = useThemeColors()
  const arr = Array.isArray(children) ? children.filter(Boolean) : [children]
  const count = arr.length
  if (count <= 1) return <View>{arr[0]}</View>

  // `order` è l'ordine ciclico corrente degli indici originali — order[0] è
  // sempre la card in cima allo stack, order[1] quella subito dietro, ecc.
  const [order, setOrder] = useState(() => arr.map((_, i) => i))

  const panX = useSharedValue(0)
  const panY = useSharedValue(0)
  // 0 = tutto a riposo, 1 = scambio (card davanti <-> card dietro) completato.
  // Guida in parallelo sia la posizione/opacità della peek (che avanza verso
  // il centro) sia l'overlay di scurimento della card davanti (che indietreggia
  // verso l'angolo) — le due card si "accompagnano" invece che la prima
  // sparire e la seconda comparire di colpo.
  const crossfade = useSharedValue(0)

  const rotateFront = () => {
    setOrder(prev => [...prev.slice(1), prev[0]])
  }

  // Reset PRIMA di setOrder (non dopo): setOrder scatena un re-render e, se
  // il reset dei valori condivisi arrivasse dopo, per un frame il nuovo
  // contenuto (ex-peek) erediterebbe ancora la vecchia posizione/opacità
  // della card appena spostata — scatto visibile invece di scambio pulito.
  const resetAndRotate = () => {
    panX.value = 0
    panY.value = 0
    crossfade.value = 0
    rotateFront()
  }

  // activeOffsetX: il pan si attiva solo dopo 12px di movimento orizzontale
  // (sotto quella soglia il tocco passa attraverso, così il bottone VAI
  // dentro la card resta cliccabile normalmente — stesso ruolo della soglia
  // di 12px che avevamo con PanResponder). failOffsetY: se il dito si sposta
  // in verticale più di 15px prima di superare la soglia orizzontale, il
  // gesto fallisce e cede il controllo a un eventuale scroll della pagina.
  const pan = Gesture.Pan()
    .activeOffsetX([-12, 12])
    .failOffsetY([-15, 15])
    .onUpdate((e) => {
      panX.value = e.translationX
      panY.value = e.translationY
      crossfade.value = Math.min(Math.abs(e.translationX) / SWIPE_THRESHOLD, 1)
    })
    .onFinalize((e) => {
      if (Math.abs(e.translationX) > SWIPE_THRESHOLD) {
        // Durata FISSA (non spring): dovendo "tornare indietro" verso un
        // bersaglio vicino (l'angolo) partendo da dove è stato rilasciato il
        // dito, uno spring poteva metabolizzare la velocità in arrivo con
        // rimbalzi e impiegare un tempo imprevedibile prima di considerarsi a
        // riposo — la card restava storta a metà strada per un po' prima
        // dello scatto finale. Con una durata fissa lo scambio si completa
        // sempre nello stesso tempo, indipendentemente da quanto/come è
        // stato trascinato.
        panX.value = withTiming(STACK_OFFSET_X, { duration: SWAP_DURATION, easing: Easing.out(Easing.cubic) })
        panY.value = withTiming(STACK_OFFSET_Y, { duration: SWAP_DURATION, easing: Easing.out(Easing.cubic) })
        crossfade.value = withTiming(1, { duration: SWAP_DURATION, easing: Easing.out(Easing.cubic) }, (finished) => {
          if (finished) runOnJS(resetAndRotate)()
        })
      } else {
        panX.value = withSpring(0, { damping: 16, stiffness: 180 })
        panY.value = withSpring(0, { damping: 16, stiffness: 180 })
        crossfade.value = withSpring(0, { damping: 16, stiffness: 180 })
      }
    })

  // La card davanti, una volta superata la soglia, deve comunque percorrere
  // tutta la distanza da dove l'hai trascinata fino all'angolo — ed essendo
  // opaca e grande quanto lo schermo, per tutto quel tragitto COPRE
  // fisicamente la peek che nel frattempo è già arrivata (a piena opacità)
  // al centro. Il risultato è che la peek sembra "sparire e ricomparire":
  // non sparisce, viene coperta e poi scoperta. Qui la facciamo anche
  // rimpicciolire e diventare parzialmente trasparente mentre `crossfade`
  // avanza, così la peek sottostante comincia a intravedersi PRIMA che la
  // card davanti abbia finito il tragitto, invece di essere nascosta fino
  // all'ultimo istante.
  const frontStyle = useAnimatedStyle(() => {
    const rotateDeg = interpolate(panX.value, [-200, 0, 200], [-8, 0, 8], Extrapolation.CLAMP)
    const scale = interpolate(crossfade.value, [0, 1], [1, 0.75], Extrapolation.CLAMP)
    const opacity = interpolate(crossfade.value, [0, 1], [1, 0.12], Extrapolation.CLAMP)
    return {
      opacity,
      transform: [
        { translateX: panX.value },
        { translateY: panY.value },
        { rotate: `${rotateDeg}deg` },
        { scale },
      ],
    }
  })

  const frontOverlayStyle = useAnimatedStyle(() => ({
    opacity: interpolate(crossfade.value, [0, 1], [0, 1]),
  }))

  // Peek e card davanti si scambiano di posto in modo speculare, guidate
  // dallo stesso `crossfade` (0 = riposo, 1 = scambio completato) invece
  // che dalla posizione grezza del drag: la destinazione finale della card
  // davanti (STACK_OFFSET_X/Y) è un valore piccolo e fisso, ben dentro
  // SWIPE_THRESHOLD, quindi legarla alla posizione avrebbe lasciato
  // l'overlay a metà scurimento invece che completo a riposo.
  const peekStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: interpolate(crossfade.value, [0, 1], [STACK_OFFSET_X, 0]) },
      { translateY: interpolate(crossfade.value, [0, 1], [0, STACK_OFFSET_Y]) },
    ],
  }))

  const peekOverlayStyle = useAnimatedStyle(() => ({
    opacity: interpolate(crossfade.value, [0, 1], [1, 0]),
  }))

  return (
    <View>
      <View style={styles.stackWrap}>
        {order.length > 1 && (
          <ReanimatedAnimated.View
            // key FISSA per ruolo (non legata al contenuto/order): con
            // key={`peek-${order[1]}`} ogni rotazione cambiava la stringa
            // della key (l'indice sotto cambia), quindi React trattava lo
            // slot come un elemento NUOVO — smontava l'istanza nativa
            // (compreso il suo GestureDetector) e ne montava una da zero,
            // invece di aggiornare semplicemente il contenuto mostrato.
            // Era quasi certamente la vera causa dello sparire-e-ricomparire
            // segnalato fin dall'inizio, indipendente da quale animazione
            // usassimo sotto: lo slot è un RUOLO fisso (front/peek), non
            // un contenuto — la key deve riflettere quello, non l'indice
            // della card che ci sta dentro in un dato momento.
            key="peek"
            pointerEvents="none"
            style={[
              styles.stackCard,
              styles.cardPeekShadow,
              { backgroundColor: tc.bg },
              peekStyle,
            ]}
          >
            {arr[order[1]]}
            <ReanimatedAnimated.View style={[styles.peekOverlay, peekOverlayStyle]} />
          </ReanimatedAnimated.View>
        )}

        <GestureDetector gesture={pan}>
          <ReanimatedAnimated.View
            key="front"
            style={[
              styles.frontCard,
              styles.cardActiveShadow,
              { backgroundColor: tc.bg, marginTop: STACK_OFFSET_Y },
              frontStyle,
            ]}
          >
            {arr[order[0]]}
            <ReanimatedAnimated.View pointerEvents="none" style={[styles.peekOverlay, frontOverlayStyle]} />
          </ReanimatedAnimated.View>
        </GestureDetector>
      </View>

      {/* Dots grandi e ovali, specifici di questo carosello — non tondi
          come quelli del Carousel normale, per distinguerli visivamente. */}
      <View style={styles.pillDots}>
        {arr.map((_, i) => (
          <View
            key={i}
            style={[
              styles.pillDot,
              { backgroundColor: order[0] === i ? tc.rosa : tc.dotInactive },
              order[0] === i && styles.pillDotActive,
            ]}
          />
        ))}
      </View>
    </View>
  )
}

// ─── ProssimaCard ─────────────────────────────────────────────────────
export function ProssimaCard({ item }) {
  const { t } = useTranslation()
  const tc = useThemeColors()

  const tappaLabel = item.id_type_race === 3
    ? t('common.one_day_race')
    : item.stage ? `${t('common.stage')} ${item.stage}` : null

  const dateStr = item.stage_date ? (() => {
    const d = new Date(item.stage_date)
    const diff = Math.round((d - new Date(romeToday())) / 86400000)   // entrambe a mezzanotte UTC: giorni di calendario a Roma
    if (diff === 1) return t('common.tomorrow')
    if (diff <= 7) return t('common.in_days', { count: diff })
    return d.toLocaleDateString()
  })() : ''

  const timeStr = item.time ? item.time.substring(0, 5) : null

  return (
    <View style={[styles.card, { backgroundColor: tc.bgCard, borderColor: tc.rosaBorder }]}>
      <Text style={[styles.prossimaName, { color: tc.text }]}>{item.race_name}</Text>
      {tappaLabel ? <Text style={[styles.prossimaSub, { color: tc.textMuted }]}>{tappaLabel}</Text> : null}
      <View style={styles.prossimaDateRow}>
        {dateStr ? <Text style={[styles.prossimaDate, { color: tc.rosaText }]}>{dateStr}</Text> : null}
        {timeStr ? <Text style={[styles.prossimaTime, { color: tc.textMuted }]}>{t('common.at_hours', { hours: timeStr })}</Text> : null}
      </View>
    </View>
  )
}

// ─── Styles ───────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  cronoWrap: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'center',
    paddingVertical: 16,
    marginVertical: 10,
  },
  cronoNum: {
    fontFamily: fonts.dseg7b,
    letterSpacing: 0,
    textAlign: 'center',

  },
  cronoSep: {
    fontFamily: fonts.dseg7b,
    opacity: 0.5,
    marginHorizontal: 3,
  },
  dots: { flexDirection: 'row', justifyContent: 'center', gap: 8, marginTop: 12 },
  dot: { height: 8, borderRadius: 4, width: 8 },
  dotActive: { width: 24 },
  // Dots grandi e ovali per PickStackCarousel, non tondi come quelli sopra
  pillDots: { flexDirection: 'row', justifyContent: 'center', gap: 10, marginTop: 16 },
  pillDot: { height: 8, width: 28, borderRadius: 7 },
  pillDotActive: { width: 56 },
  // Contenitore dello stack: altezza fissata via `minHeight` non basta
  // perché le card hanno altezza variabile — usiamo semplicemente un margin
  // extra in basso/lato per far spazio all'offset della card che sbircia,
  // così non viene tagliata dal contenuto successivo della pagina.
  stackWrap: {
    position: 'relative',
    marginBottom: 6,// STACK_OFFSET_Y + 6,
    marginRight: STACK_OFFSET_X + 6,
  },
  stackCard: {
    position: 'absolute',
    top: 0, left: 0, right: 0,
    borderRadius: 14,
  },
  frontCard: {
    borderRadius: 14,
  },
  peekOverlay: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 14,
    backgroundColor: 'rgba(120,120,130,0.35)',
  },
  cardActiveShadow: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.14,
    shadowRadius: 12,
    elevation: 5,
  },
  cardPeekShadow: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.08,
    shadowRadius: 6,
    elevation: 2,
  },
  card: { borderRadius: 14, borderWidth: 1, padding: 16 },
  prossimaName: {
    fontFamily: fonts.condensedBold,
    fontSize: 18,
    marginBottom: 2 },
  prossimaSub: { fontSize: 13, marginBottom: 8 },
  prossimaDateRow: { flexDirection: 'row', alignItems: 'center', gap: 16, marginTop: 8 },
  prossimaDate: { fontSize: 14, fontWeight: '700' },
  prossimaTime: { fontSize: 14 },
})